import { pluralRulesMap } from './locales';
import type { I18nConfig } from './plural-rules.types';

/**
 * Enhances i18n configuration with CLDR-based plural rules.
 *
 * Uses CLDR cardinal categories (one, few, many, other) with an explicit zero extension
 * for improved UX in languages where "no items" deserves special phrasing.
 *
 * Supported locales and their plural forms:
 *
 * **Polish (pl, pl-PL)** - 4 forms (CLDR: one, few, many + zero extension):
 * - Form 0 (zero): n = 0 → "brak pojazdów" (zero extension, not in CLDR)
 * - Form 1 (one): n = 1 → "1 pojazd" (CLDR: one)
 * - Form 2 (few): n = 2-4, 22-24, 32-34, ... (excluding 11-19) → "2 pojazdy" (CLDR: few)
 * - Form 3 (many): 5+, 11-19, 111-119, ... → "5 pojazdów" (CLDR: many)
 *
 * Optional 5th form for fractions: 1.5, 2.3, etc. → "1.5 pojazdu" (CLDR: other)
 *
 * Note: CLDR categorizes 0 as "many", but we provide explicit zero form for UX flexibility.
 *
 * @param options - i18n configuration
 * @returns Enhanced configuration with plural rules
 *
 * @example
 * import { createI18n } from 'vue-i18n';
 * import { withPluralRules } from '@abax/platform';
 *
 * // Polish example with 4 forms (zero, one, few, many)
 * const i18n = createI18n(withPluralRules({
 *   locale: 'pl-PL',
 *   messages: {
 *     'pl-PL': {
 *       cars: 'brak pojazdów | {n} pojazd | {n} pojazdy | {n} pojazdów'
 *     }
 *   }
 * }));
 *
 * // Usage in component:
 * t('cars', { n: 0 })  // "brak pojazdów" (form 0 - zero)
 * t('cars', { n: 1 })  // "1 pojazd" (form 1 - one)
 * t('cars', { n: 2 })  // "2 pojazdy" (form 2 - few)
 * t('cars', { n: 5 })  // "5 pojazdów" (form 3 - many)
 * t('cars', { n: 23 }) // "23 pojazdy" (form 2 - few)
 */
export function withPluralRules<T extends I18nConfig & { legacy: false }>(
  options: T,
): T & { pluralRules: NonNullable<T['pluralRules']> & typeof pluralRulesMap; legacy: false };
export function withPluralRules<T extends I18nConfig>(
  options: T,
): T & { pluralRules: NonNullable<T['pluralRules']> & typeof pluralRulesMap };
export function withPluralRules<T extends I18nConfig>(
  options: T,
): T & { pluralRules: NonNullable<T['pluralRules']> & typeof pluralRulesMap } {
  return {
    ...options,
    pluralRules: {
      ...options.pluralRules,
      ...pluralRulesMap,
    },
  };
}
